/**
 * PrePrintOverlay - Full-screen overlay for pre-print batch garments
 *
 * Shown INSTEAD of PlacementOverlay when a pre-print garment is caught.
 * Pre-print garments don't go to bins or packing shelves — they go to
 * the pre-print stock area.
 *
 * Design:
 * - Violet/purple background (deliberately different from bin blue / shelf green)
 * - Large "PRE-PRINT STOCK" text with "Do Not Ship" warning
 * - Product name + SKU displayed prominently
 * - Auto-dismiss after 10 seconds
 * - Manual "Got it" button to dismiss immediately
 */
import { useEffect, useRef } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogOverlay,
  AlertDialogPortal,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { playSuccessBeep } from '@/lib/sounds';
import { cn } from '@/lib/utils';
import { Package } from 'lucide-react';
import { ZoomImg } from '@/components/production/HoverZoom';

interface PrePrintOverlayProps {
  open: boolean;
  productName: string;
  sku: string;
  imageUrl?: string | null;
  onDismiss: () => void;
}

const AUTO_DISMISS_MS = 10000;

export function PrePrintOverlay({
  open,
  productName,
  sku,
  imageUrl,
  onDismiss,
}: PrePrintOverlayProps) {
  const isDismissingRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep latest onDismiss in a ref so effect below can fire ONLY on open transitions.
  // Previously onDismiss was in the deps array, which caused the effect to re-run
  // every time the parent re-rendered with a fresh inline callback — playing the
  // beep 2-3 times per catch and resetting the auto-dismiss timer.
  const onDismissRef = useRef(onDismiss);
  useEffect(() => {
    onDismissRef.current = onDismiss;
  }, [onDismiss]);

  // Play success beep on mount + start auto-dismiss timer.
  // Depends ONLY on `open` — fires once per false→true transition.
  useEffect(() => {
    if (!open) {
      isDismissingRef.current = false;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    playSuccessBeep();

    timerRef.current = setTimeout(() => {
      if (!isDismissingRef.current) {
        isDismissingRef.current = true;
        onDismissRef.current();
      }
    }, AUTO_DISMISS_MS);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [open]);

  // Keyboard: Enter or Escape to dismiss. Depends only on `open` — uses the
  // ref above for the latest onDismiss so a fresh inline callback doesn't
  // rebind the listener on every render.
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.altKey || e.metaKey) return;

      if (e.key === 'Enter' || e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        if (!isDismissingRef.current) {
          isDismissingRef.current = true;
          onDismissRef.current();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [open]);

  if (!open) return null;

  const handleManualDismiss = () => {
    if (isDismissingRef.current) return;
    isDismissingRef.current = true;
    onDismiss();
  };

  return (
    <AlertDialog open={open}>
      <AlertDialogPortal>
        {/* Full-screen violet background */}
        <AlertDialogOverlay
          className={cn(
            'fixed inset-0 z-50',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:duration-200',
            'bg-gradient-to-b from-violet-950 via-violet-900 to-violet-950',
          )}
        />
        <AlertDialogContent
          className={cn(
            'fixed inset-0 z-50 flex items-center justify-center',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=open]:duration-200',
            'border-0 bg-transparent p-4 shadow-none max-w-none w-auto',
            'translate-x-0 translate-y-0 left-0 top-0',
          )}
        >
          <div className="w-full max-w-4xl mx-auto flex flex-col items-center gap-6">
            {/* Icon + label */}
            <div className="text-center space-y-3">
              <div className="flex items-center justify-center gap-3">
                <Package className="h-7 w-7 text-violet-300" aria-hidden="true" />
                <p className="text-2xl uppercase tracking-widest text-violet-300 font-semibold">
                  Pre-Print Stock
                </p>
              </div>
              <p className="text-7xl sm:text-8xl font-black text-white animate-glow-violet">
                DO NOT SHIP
              </p>
            </div>

            {/* Instruction */}
            <p className="text-2xl sm:text-3xl text-violet-200 text-center font-medium">
              Place with pre-printed stock
            </p>

            {/* Product details — thumbnail + name + SKU */}
            <div className="w-full rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 px-6 py-5">
              <div className="flex items-center gap-5">
                {imageUrl ? (
                  <ZoomImg
                    src={imageUrl}
                    alt={productName}
                    className="w-28 h-28 rounded-lg object-cover flex-shrink-0 border border-white/30"
                  />
                ) : (
                  <div className="w-28 h-28 rounded-lg bg-violet-800/40 border border-white/20 flex-shrink-0 flex items-center justify-center">
                    <Package className="h-10 w-10 text-violet-300" aria-hidden="true" />
                  </div>
                )}
                <div className="min-w-0 flex-1 text-left space-y-1">
                  <p className="text-2xl font-bold text-white leading-tight">{productName}</p>
                  <p className="text-lg text-violet-300 font-mono break-all">{sku}</p>
                </div>
              </div>
            </div>

            {/* Dismiss button */}
            <Button
              variant="ghost"
              size="lg"
              className="w-full h-20 text-2xl font-semibold rounded-xl bg-violet-600 text-white hover:text-white hover:bg-violet-600 active:bg-violet-700 active:text-white"
              onClick={handleManualDismiss}
            >
              Got it
              <kbd className="ml-2 text-xs font-normal px-1.5 py-0.5 rounded bg-white/15 text-white/70">
                Enter
              </kbd>
            </Button>

            {/* Auto-dismiss indicator */}
            <p className="text-sm text-violet-400/70">
              Auto-dismisses in 10 seconds
            </p>
          </div>

          {/* ARIA live region */}
          <div role="status" aria-live="assertive" className="sr-only">
            Pre-print stock. Place with pre-printed stock. Do not ship. {productName}, {sku}.
          </div>
        </AlertDialogContent>
      </AlertDialogPortal>
    </AlertDialog>
  );
}
